import { ArrowRight, MessageCircle, MapPin } from "lucide-react";
import { useI18n } from "../lib/i18n";

const HERO_IMG =
  "https://images.pexels.com/photos/31298995/pexels-photo-31298995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

export const Hero = () => {
  const { t } = useI18n();

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      {/* BG image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      />
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C] via-[#0A0A0C]/85 to-[#0A0A0C]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent" />
      <div className="absolute inset-0 stripes opacity-50" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24">
        <div className="max-w-3xl fade-up">
          <div className="inline-flex items-center gap-2 border border-[#E51E1A]/40 bg-[#E51E1A]/5 px-4 py-2 mb-8">
            <MapPin className="w-4 h-4 text-[#E51E1A]" />
            <span className="font-condensed uppercase tracking-[0.25em] text-xs text-zinc-200">
              {t.hero.kicker}
            </span>
          </div>

          <h1 className="font-headings uppercase text-white leading-[0.92] text-6xl sm:text-7xl lg:text-8xl">
            {t.hero.title1}
            <br />
            <span className="text-[#E51E1A]">{t.hero.title2}</span>
            <br />
            <span className="text-zinc-500">{t.hero.titleAccent}</span>
          </h1>

          <p className="mt-8 text-zinc-300 text-lg md:text-xl max-w-2xl leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#booking"
              data-testid="hero-cta-book"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById("booking")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center justify-center gap-3 bg-[#E51E1A] hover:bg-[#FF3B30] text-white font-headings uppercase tracking-wider text-lg px-8 py-5 transition-all hover:-translate-y-1 shadow-[6px_6px_0px_0px_rgba(255,255,255,0.08)] hover:shadow-[8px_8px_0px_0px_rgba(229,30,26,0.4)]"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="https://wa.me/14175140081"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-3 border-2 border-zinc-700 hover:border-[#25D366] hover:text-[#25D366] text-white font-headings uppercase tracking-wider text-lg px-8 py-5 transition"
            >
              <MessageCircle className="w-5 h-5" />
              {t.hero.ctaSecondary}
            </a>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 max-w-2xl border-t border-zinc-800">
            {t.hero.stats.map((s, i) => (
              <div
                key={i}
                className={`py-6 px-4 ${i !== 0 ? "border-l border-zinc-800" : ""}`}
              >
                <div className="font-headings text-3xl md:text-4xl text-[#E51E1A]">
                  {s.k}
                </div>
                <div className="font-condensed uppercase text-xs tracking-widest text-zinc-400 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Big background number */}
      <div className="hidden lg:block absolute right-[-40px] bottom-10 font-headings text-[200px] xl:text-[280px] leading-none text-white/[0.04] select-none pointer-events-none">
        TORQUE
      </div>
    </section>
  );
};
